// AboutComponent.js
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import aboutImage from "../assets/about.png";

const AboutComponent = () => {
  return (
    <section className="mx-4 md:mx-24 bg-white py-8 md:py-20 px-8 md:px-16 rounded-3xl mt-4 md:mt-16">
      <h2 className="text-2xl md:text-3xl font-bold font-inter text-acBlack mb-4">
        About Us
      </h2>
      <p className="text-md text-acGray font-light mb-8">
        Practical immigration consulting for individuals and families-delivered
        from London and New York, across the world.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-12">
        <motion.img
          src={aboutImage}
          alt="About Us"
          className="w-full max-w-md mx-auto lg:mx-0"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        />

        <motion.div
          className="text-left space-y-4"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-md text-acGray font-light">
            Visapat is a global immigration consulting practice focused
            exclusively on individuals and families. We help you choose the
            right pathway-work, study, family, permanent residence/citizenship,
            investor, or visit-and guide you from eligibility to arrival with
            clear timelines and meticulous document preparation.
          </p>

          <p className="text-md text-acGray font-light">
            Our operations are organized around two hubs for speed and coverage.
            London serves as our EMEA office, supporting clients and partners
            across Europe, the Middle East, and Africa. New York hosts our APAC
            desk, coordinating services into Asia–Pacific time zones through our
            partner network. This model lets us provide fast responses, overlap
            across multiple time zones, and secure appointments or filings as
            soon as local windows open.
          </p>

          <p className="text-md text-acGray font-light">
            We're a compliance-first, people-centred team. You'll have a
            dedicated point of contact who coordinates your case
            end-to-end-eligibility review, checklists and templates,
            translations/apostilles, online or consular submission, biometrics
            scheduling, interview prep, and post-decision steps such as
            travel/arrival checklists and local registrations (where
            applicable).
          </p>

          <p className="text-md text-acGray font-light">
            Quality control is baked into every stage. We evidence-map each
            application to current rules, proactively track deadlines, and flag
            risks early so there are no surprises. If a decision is negative, we
            reassess options or prepare an appeal/administrative review with
            licensed local counsel where required by law.
          </p>

          <p className="text-md text-acGray font-light">
            We take privacy seriously. Your documents are handled securely,
            shared on a need-to-know basis, and retained only for as long as
            needed to complete your case and meet regulatory requirements.
          </p>
        </motion.div>
      </div>

      {/* Mini Highlights */}
      <motion.div
        className="bg-gray-50 rounded-2xl p-6 mb-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold text-acBlack mb-4">
          Why Choose Visapat
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-50 0 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <p className="font-medium text-acBlack">Clarity</p>
              <p className="text-sm text-acGray">
                Honest eligibility calls, transparent fees, realistic timelines.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-50 0 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <p className="font-medium text-acBlack">Care</p>
              <p className="text-sm text-acGray">
                One case owner; WhatsApp and email support with 24–72h response.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-50 0 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <p className="font-medium text-acBlack">Compliance</p>
              <p className="text-sm text-acGray">
                Applications aligned to current evidence standards.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-50 0 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <p className="font-medium text-acBlack">Coverage</p>
              <p className="text-sm text-acGray">
                London (EMEA) and New York (APAC desk) enable
                near-follow-the-sun support.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-50 0 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <p className="font-medium text-acBlack">Continuity</p>
              <p className="text-sm text-acGray">
                From first visa to renewals, PR/citizenship, and family
                pathways.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Offices & Hours */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-acBlack mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-blue-600" />
            United Kingdom- EMEA Office
          </h4>
          <div className="space-y-2 text-sm text-acGray">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>Mon–Fri, 09:00–18:00 GMT/BST</span>
            </div>
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              <span>39 George Road, Edgbaston, Birmingham, England, B15 1PL</span>
            </div>
            <div className="flex items-center">
              <Phone className="w-4 h-4 mr-2" />
              <span>+44 (0)75 3716 9698</span>
            </div>
            <div className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              <span>emea@visapat.com</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-acBlack mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-blue-600" />
            New York - APAC Desk
          </h4>
          <div className="space-y-2 text-sm text-acGray">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>Mon–Fri, 09:00–18:00 ET; evening ET overlap with APAC</span>
            </div>
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              <span>
                477 Madison Ave 6th floor, New York, NY 10022, United States
              </span>
            </div>
            <div className="flex items-center">
              <Phone className="w-4 h-4 mr-2" />
              <span>+1 (332) 209-5612</span>
            </div>
            <div className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              <span>apac@visapat.com</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTAs */}
      <motion.div
        className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <Link
          to="/about"
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
        >
          Learn more
        </Link>
        <Link
          to="/contact"
          className="text-blue-600 hover:text-blue-800 font-medium underline transition-colors"
        >
          Contact us
        </Link>
      </motion.div>
    </section>
  );
};

export default AboutComponent;
